import {
  Button,
  FormLabel,
  FormControl,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  useDisclosure,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { Icon } from '~/packages/common-frontend/components/icons';
import { MultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import {
  Select,
  formatSelectOptions,
} from '~/packages/common-frontend/components/Select';

// Types
const audienceFilterOptions = ['all', 'internal', 'external'] as const;
type AudienceFilterType = typeof audienceFilterOptions[number];

export type FilterType = {
  audienceFilter: AudienceFilterType;
  showParticipantFilter: boolean;
  titleText: string;
  externalParticipantIds: string[];
};

// Exports
export const FilterButton = ({
  externalOrgOptions,
  onApply,
  currentFilter,
}: {
  externalOrgOptions: { value: string; label: string }[];
  onApply: (newCurrentFilter: FilterType) => void;
  currentFilter: FilterType | undefined;
}) => {
  // audience filter
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilterType>(
    currentFilter ? currentFilter.audienceFilter : 'all',
  );

  // participant filter
  const [showParticipantFilter, setShowParticipantFilter] = useState<boolean>(
    currentFilter ? currentFilter.showParticipantFilter : true,
  );
  const [titleText, setTitleText] = useState<string>(
    currentFilter ? currentFilter.titleText : 'All Participants',
  );
  const allExternalParticipantIds = externalOrgOptions.map(
    (option) => option.value,
  );
  const [selectedParticipantIds, setSelectedExternalPartyIds] = useState<
    string[]
  >(
    currentFilter
      ? currentFilter.externalParticipantIds
      : allExternalParticipantIds,
  );

  const onChange = (newValue: string) => {
    const copyOfSelectedParticipantIds = _.clone(selectedParticipantIds);
    const index = copyOfSelectedParticipantIds.indexOf(newValue);
    if (index >= 0) {
      // only remove option if multiple are selected
      if (copyOfSelectedParticipantIds.length > 1) {
        copyOfSelectedParticipantIds.splice(index, 1);
      }
    } else {
      copyOfSelectedParticipantIds.push(newValue);
    }
    setSelectedExternalPartyIds(copyOfSelectedParticipantIds);

    if (copyOfSelectedParticipantIds.length > 0) {
      if (copyOfSelectedParticipantIds.length === externalOrgOptions.length) {
        setTitleText('All Participants');
      } else {
        const selectedOptions = externalOrgOptions.filter((option) =>
          copyOfSelectedParticipantIds.includes(option.value),
        );
        const labels = selectedOptions.map((option) =>
          _.startCase(option.label),
        );
        setTitleText(labels.join(', '));
      }
    } else {
      setTitleText('Select Participants');
    }
  };

  // use effect on select change
  useEffect(() => {
    if (audienceFilter === 'internal') {
      setShowParticipantFilter(false);
      setSelectedExternalPartyIds([]);
    } else {
      setShowParticipantFilter(true);
      setSelectedExternalPartyIds((curr) => {
        if (curr.length === 0) {
          return allExternalParticipantIds;
        }
        return curr;
      });
    }
  }, [allExternalParticipantIds, audienceFilter]);

  const onSubmit = useCallback(() => {
    switch (audienceFilter) {
      case 'all':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'external':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'internal':
        return onApply({
          audienceFilter,
          showParticipantFilter: false,
          titleText,
          externalParticipantIds: [],
        });
      default:
        return finleyAssertUnreachable(audienceFilter);
    }
  }, [
    audienceFilter,
    onApply,
    selectedParticipantIds,
    showParticipantFilter,
    titleText,
  ]);

  const onClear = useCallback(() => {
    setAudienceFilter(currentFilter ? currentFilter.audienceFilter : 'all');
    setShowParticipantFilter(
      currentFilter ? currentFilter.showParticipantFilter : true,
    );
    setTitleText(currentFilter ? currentFilter.titleText : 'All Participants');
    setSelectedExternalPartyIds(
      currentFilter
        ? currentFilter.externalParticipantIds
        : allExternalParticipantIds,
    );
  }, [allExternalParticipantIds, currentFilter]);

  const popoverControls = useDisclosure();
  return (
    <Popover {...popoverControls} placement="bottom-end">
      <PopoverTrigger>
        <Button
          leftIcon={<Icon icon="filter" />}
          variant="secondary-muted"
          flexShrink={0}
          size="sm"
        >
          Filter
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody display="flex" flexDir="column" gap="1rem">
          <FormControl>
            <FormLabel>Audience</FormLabel>
            <Select
              value={audienceFilter}
              onChange={(newVal) => {
                if (newVal) {
                  setAudienceFilter(newVal);
                }
              }}
              options={formatSelectOptions(audienceFilterOptions, 'title')}
            />
          </FormControl>
          {showParticipantFilter && (
            <FormControl>
              <FormLabel>Participants</FormLabel>
              <MultiSelect
                options={externalOrgOptions}
                onChange={onChange}
                selectedOptions={selectedParticipantIds}
                titleText={titleText}
              />
            </FormControl>
          )}
        </PopoverBody>
        <PopoverFooter display="flex" gap="1rem">
          <Button
            flexGrow={1}
            variant="secondary"
            colorScheme="v2-aurora"
            onClick={() => {
              onClear();
              popoverControls.onClose();
            }}
          >
            Clear
          </Button>
          <Button
            flexGrow={1}
            variant="primary"
            colorScheme="v2-aurora"
            onClick={() => {
              onSubmit();
              popoverControls.onClose();
            }}
          >
            Apply
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};
